import { useState, useRef, useEffect } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import { X, PaperPlaneTilt, ShoppingCart, CalendarCheck, ChatCircle, WhatsappLogo, Phone, User, EnvelopeSimple, Warning } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Validate Indian mobile number: must be exactly 10 digits (after stripping +91/91/0 prefix)
function validateIndianMobile(raw) {
  const cleaned = raw.replace(/[\s\-()]/g, '');
  let digits = cleaned;
  if (digits.startsWith('+91')) digits = digits.slice(3);
  else if (digits.startsWith('91') && digits.length > 10) digits = digits.slice(2);
  else if (digits.startsWith('0')) digits = digits.slice(1);
  if (!/^\d{10}$/.test(digits)) return null;
  if (!/^[6-9]/.test(digits)) return null; // Indian mobiles start with 6-9
  return '+91' + digits;
}

const parseMessageForActions = (text) => {
  const cartUrlRegex = /portl\.co\/cart\/\?add-to-cart=\d+&quantity=\d+/g;
  const cartUrls = text.match(cartUrlRegex) || [];
  const bookingUrlMatch = text.match(/https?:\/\/[^\s]+calendly[^\s]+|https?:\/\/calendar\.google\.com[^\s]+/gi);
  const bookingUrls = bookingUrlMatch || [];
  return { cartUrls, bookingUrls };
};

const MessageBubble = ({ message, onBuyNow, onBookDemo }) => {
  const isUser = message.role === "user";
  const { cartUrls, bookingUrls } = parseMessageForActions(message.content);

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} mb-3`}>
      <div
        className={`max-w-[85%] rounded-lg px-4 py-2.5 ${
          isUser
            ? "bg-[#E85D24] text-white"
            : "bg-[#1A1A1A] text-white border border-[#2A2A2A]"
        }`}
      >
        <div className="text-sm leading-relaxed prose prose-invert prose-sm max-w-none
          prose-strong:text-[#E85D24] prose-strong:font-bold
          prose-p:my-1 prose-ul:my-1 prose-li:my-0.5
          prose-a:text-[#E85D24] prose-a:no-underline hover:prose-a:underline
          prose-table:w-full prose-table:my-2 prose-table:border-collapse
          prose-th:bg-[#1A1A1A] prose-th:text-[#E85D24] prose-th:font-bold prose-th:text-left prose-th:px-3 prose-th:py-2 prose-th:border prose-th:border-[#333]
          prose-td:px-3 prose-td:py-2 prose-td:border prose-td:border-[#333] prose-td:text-white
          prose-tr:even:bg-[#111]
          [&_table]:w-full [&_table]:my-2 [&_table]:border-collapse [&_table]:text-sm
          [&_th]:bg-[#1A1A1A] [&_th]:text-[#E85D24] [&_th]:font-bold [&_th]:text-left [&_th]:px-3 [&_th]:py-2 [&_th]:border [&_th]:border-[#333]
          [&_td]:px-3 [&_td]:py-2 [&_td]:border [&_td]:border-[#333] [&_td]:text-white
          [&_tr:nth-child(even)]:bg-[#111]
          [&_b]:text-[#E85D24] [&_b]:font-bold">
          <ReactMarkdown rehypePlugins={[rehypeRaw]}>{message.content}</ReactMarkdown>
        </div>

        {!isUser && (cartUrls.length > 0 || bookingUrls.length > 0) && (
          <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-white/10">
            {cartUrls.map((url, idx) => (
              <button
                key={`cart-${idx}`}
                onClick={() => onBuyNow(`https://${url}`)}
                data-testid="buy-now-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#E85D24] text-white text-xs font-bold rounded hover:opacity-85 transition-opacity"
              >
                <ShoppingCart size={14} weight="bold" />
                Buy Now
              </button>
            ))}
            {bookingUrls.map((url, idx) => (
              <button
                key={`book-${idx}`}
                onClick={() => onBookDemo(url)}
                data-testid="book-demo-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#FFD60A] text-black text-xs font-bold rounded hover:opacity-85 transition-opacity"
              >
                <CalendarCheck size={14} weight="bold" />
                Book Your Demo
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const ChatWidget = ({ position = "bottom-right", apiUrl = API }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [streamingText, setStreamingText] = useState("");
  const [exchangeCount, setExchangeCount] = useState(0);
  const [showPhonePrompt, setShowPhonePrompt] = useState(false);
  const [handoffComplete, setHandoffComplete] = useState(false);
  const [handoffWaUrl, setHandoffWaUrl] = useState("");
  const [phoneInput, setPhoneInput] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [visitorPhone, setVisitorPhone] = useState("");
  const [visitorName, setVisitorName] = useState("");
  const [visitorEmail, setVisitorEmail] = useState("");
  const [showCallbackForm, setShowCallbackForm] = useState(false);
  const [callbackName, setCallbackName] = useState("");
  const [callbackEmail, setCallbackEmail] = useState("");
  const [callbackSubmitting, setCallbackSubmitting] = useState(false);
  const [callbackDone, setCallbackDone] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const phoneRef = useRef(null);

  useEffect(() => {
    const savedSessionId = localStorage.getItem("portl_chat_session");
    if (savedSessionId) {
      setSessionId(savedSessionId);
      loadMessages(savedSessionId);
    }
  }, []);

  const loadMessages = async (sid) => {
    try {
      const response = await axios.get(`${apiUrl}/sessions/${sid}/messages`);
      // Preserve the server-side id so React keying stays stable across re-renders.
      const msgs = response.data.map(m => ({ id: m.id, role: m.role, content: m.content }));
      setMessages(msgs);
      setExchangeCount(msgs.filter(m => m.role === "user").length);
    } catch (err) {
      console.error("Failed to load messages:", err);
    }
  };

  // Generate a stable client-side id for newly appended messages.
  const mkLocalId = () => `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamingText]);

  useEffect(() => {
    if (isOpen && !showPhonePrompt && !showCallbackForm) inputRef.current?.focus();
  }, [isOpen, showPhonePrompt, showCallbackForm]);

  useEffect(() => {
    if (showPhonePrompt) phoneRef.current?.focus();
  }, [showPhonePrompt]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;
    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { id: mkLocalId(), role: "user", content: userMessage }]);
    setExchangeCount(prev => prev + 1);
    setIsLoading(true);
    setStreamingText("");

    try {
      const response = await fetch(`${apiUrl}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: userMessage, channel: "website" })
      });
      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let fullText = "";
      // Buffer for incomplete SSE lines that span chunk boundaries.
      // Each TCP read can split a "data: {...}\n\n" line at any byte; if we
      // parse before the newline arrives we lose the partial JSON (silent
      // dropped text). The buffer lets us only parse complete lines.
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          // Flush any final bytes still sitting in the decoder.
          buffer += decoder.decode();
          break;
        }
        // `stream: true` keeps partial multi-byte UTF-8 sequences intact
        // across read boundaries (e.g. emoji, ₹ symbol, em-dash).
        buffer += decoder.decode(value, { stream: true });
        // SSE events are separated by `\n\n` but each line ends with `\n`.
        // Process every complete line we have so far, keep the rest in buffer.
        const nlIdx = (() => {
          let last = -1;
          for (let i = buffer.length - 1; i >= 0; i--) {
            if (buffer[i] === "\n") { last = i; break; }
          }
          return last;
        })();
        if (nlIdx < 0) continue;          // no complete line yet
        const ready = buffer.slice(0, nlIdx);
        buffer = buffer.slice(nlIdx + 1);
        for (const line of ready.split("\n")) {
          if (!line.startsWith("data: ")) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === "text") {
              fullText += data.content;
              setStreamingText(fullText);
            } else if (data.type === "done") {
              setSessionId(data.session_id);
              localStorage.setItem("portl_chat_session", data.session_id);
              if (data.visitor_phone) {
                setVisitorPhone(data.visitor_phone);
                setPhoneInput(data.visitor_phone);
              }
              if (data.visitor_name) setVisitorName(data.visitor_name);
              if (data.visitor_email) setVisitorEmail(data.visitor_email);
              if (data.handoff && data.handoff.ready && data.handoff.wa_url) {
                setHandoffComplete(true);
                setHandoffWaUrl(data.handoff.wa_url);
                setShowPhonePrompt(false);
              }
            }
          } catch (e) {
            // Truly malformed line (not a partial — we only parse complete
            // lines now). Log and continue.
            console.debug("ChatWidget: malformed SSE line", line, e);
          }
        }
      }
      // Process any final complete line still sitting in the buffer after the
      // stream ends (servers sometimes don't emit a trailing newline).
      if (buffer.trim().startsWith("data: ")) {
        try {
          const data = JSON.parse(buffer.trim().slice(6));
          if (data.type === "text") { fullText += data.content; }
        } catch { /* ignore — final incomplete frame */ }
      }
      setMessages(prev => [...prev, { id: mkLocalId(), role: "assistant", content: fullText }]);
      setStreamingText("");
    } catch (err) {
      console.error("Chat error:", err);
      setMessages(prev => [...prev, { id: mkLocalId(), role: "assistant", content: "I apologize, but I'm having trouble connecting. Please try again in a moment." }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBuyNow = (url) => window.open(url, "_blank");

  const handleBookDemo = async (url) => {
    if (sessionId) {
      try {
        await axios.post(`${apiUrl}/demo-booked`, { session_id: sessionId });
      } catch (err) {
        // Non-fatal — the user is being routed to the booking page either way.
        console.error("ChatWidget.handleBookDemo: demo-booked notify failed", err);
      }
    }
    window.open(url, "_blank");
  };

  const handlePhoneInputChange = (e) => {
    const val = e.target.value;
    // Allow only digits, +, spaces, hyphens
    if (/^[0-9+\-\s()]*$/.test(val)) {
      setPhoneInput(val);
      setPhoneError("");
    }
  };

  const handleWhatsAppHandoff = async () => {
    const phone = phoneInput.trim();
    if (!phone) return;

    const validated = validateIndianMobile(phone);
    if (!validated) {
      setPhoneError("Please enter a valid 10-digit Indian mobile number");
      return;
    }

    setPhoneError("");
    setShowPhonePrompt(false);
    setHandoffComplete(true);
    setIsLoading(true);

    try {
      await axios.post(`${apiUrl}/initiate-whatsapp-handoff`, {
        session_id: sessionId,
        visitor_phone: validated
      });

      setMessages(prev => [...prev, {
        id: mkLocalId(),
        role: "assistant",
        content: "Perfect. Our team has a full summary of our conversation and will reach out on WhatsApp shortly. You're in good hands.\n\nIs there anything else I can help you with?"
      }]);
    } catch (err) {
      console.error("Handoff error:", err);
      setMessages(prev => [...prev, { id: mkLocalId(), role: "assistant", content: "I had trouble connecting. Please try again." }]);
      setHandoffComplete(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChatOnWhatsApp = () => {
    if (handoffWaUrl) {
      window.open(handoffWaUrl, "_blank");
      return;
    }
    const watiNumber = (process.env.REACT_APP_WATI_NUMBER || "").replace(/[^0-9]/g, "");
    if (watiNumber) {
      const waUrl = `https://wa.me/${watiNumber}?text=${encodeURIComponent("Hi Portl team, Arya just briefed you about my enquiry. Looking forward to chatting.")}`;
      window.open(waUrl, "_blank");
    }
  };

  const handleRequestCallback = async () => {
    if (!callbackName.trim() || !callbackEmail.trim()) return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(callbackEmail.trim())) return;

    setCallbackSubmitting(true);
    try {
      await axios.post(`${apiUrl}/request-callback`, {
        session_id: sessionId,
        visitor_name: callbackName.trim(),
        visitor_email: callbackEmail.trim(),
        visitor_phone: visitorPhone || phoneInput.trim()
      });
      setCallbackDone(true);
      setShowCallbackForm(false);
      setMessages(prev => [...prev, {
        id: mkLocalId(),
        role: "assistant",
        content: `Thanks, ${callbackName.trim()}! Our team will call you back shortly. Is there anything else I can help with in the meantime?`
      }]);
    } catch (err) {
      console.error("Callback request error:", err);
    } finally {
      setCallbackSubmitting(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const showWhatsAppButton = exchangeCount >= 3 && !showPhonePrompt && !handoffComplete && !isLoading;

  return (
    <div className="fixed bottom-20 right-4 z-50 max-[480px]:right-3 max-[480px]:left-3 max-[480px]:bottom-[calc(16px+env(safe-area-inset-bottom,0px))]" data-testid="chat-widget">
      {isOpen && (
        <div
          className="mb-4 w-[380px] h-[540px] max-h-[80vh] bg-black border border-[#2A2A2A] rounded-xl shadow-2xl flex flex-col overflow-hidden
            max-[480px]:fixed max-[480px]:inset-x-2 max-[480px]:bottom-[calc(80px+env(safe-area-inset-bottom,0px))] max-[480px]:top-auto
            max-[480px]:w-auto max-[480px]:h-[60vh] max-[480px]:max-h-[560px] max-[480px]:min-h-[380px]"
          data-testid="chat-panel">
          {/* Use dvh on mobile for keyboard-aware sizing; the host site stays visible above for ~40% of the viewport. */}
          <style>{`@media (max-width: 480px) { [data-testid="chat-panel"] { height: 60dvh !important; } }`}</style>
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-[#0A0A0A] border-b border-[#2A2A2A]">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-[#E85D24] flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-sm">A</span>
              </div>
              <div>
                <h3 className="text-white font-semibold text-[13px]" data-testid="chat-header-title">Arya &middot; Portl Fitness Advisor</h3>
                <p className="text-[#707070] text-[11px]">Online now</p>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="p-1.5 hover:bg-[#1A1A1A] rounded transition-colors" data-testid="chat-close-btn">
              <X size={18} className="text-[#707070]" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-1">
            {messages.length === 0 && !streamingText && (
              <div className="text-center py-8">
                <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-[#E85D24] flex items-center justify-center">
                  <span className="text-white font-bold text-xl">A</span>
                </div>
                <h4 className="text-white font-semibold mb-1.5 text-[15px]">Hi, I'm Arya!</h4>
                <p className="text-[#888] text-[13px] leading-relaxed px-4">
                  Portl's Fitness &amp; Wellness Advisor. Ask me anything about UltraGym or Studio!
                </p>
              </div>
            )}

            {messages.map((msg) => (
              <MessageBubble key={msg.id} message={msg} onBuyNow={handleBuyNow} onBookDemo={handleBookDemo} />
            ))}

            {/* Post-handoff action buttons */}
            {handoffComplete && !showCallbackForm && !callbackDone && (
              <div className="flex flex-col gap-2 my-3" data-testid="handoff-actions">
                <button
                  onClick={handleChatOnWhatsApp}
                  data-testid="chat-on-whatsapp-btn"
                  className="w-full py-2.5 bg-[#25D366] text-white text-[13px] font-semibold rounded-lg flex items-center justify-center gap-2 hover:bg-[#1da851] transition-colors"
                >
                  <WhatsappLogo size={18} weight="bold" />
                  Chat on WhatsApp
                </button>
                <button
                  onClick={() => {
                    // Pre-fill known details
                    if (visitorName && !callbackName) setCallbackName(visitorName);
                    if (visitorEmail && !callbackEmail) setCallbackEmail(visitorEmail);
                    setShowCallbackForm(true);
                  }}
                  data-testid="request-callback-btn"
                  className="w-full py-2.5 border border-[#E85D24] text-[#E85D24] text-[13px] font-semibold rounded-lg flex items-center justify-center gap-2 hover:bg-[#E85D24] hover:text-white transition-colors"
                >
                  <Phone size={18} weight="bold" />
                  Request Call-back
                </button>
              </div>
            )}

            {streamingText && (
              <div className="flex justify-start mb-3">
                <div className="max-w-[85%] rounded-lg px-4 py-2.5 bg-[#1A1A1A] text-white border border-[#2A2A2A]">
                  <div className="text-sm leading-relaxed prose prose-invert prose-sm max-w-none prose-strong:text-[#E85D24] prose-strong:font-bold [&_b]:text-[#E85D24] [&_b]:font-bold">
                    <ReactMarkdown rehypePlugins={[rehypeRaw]}>{streamingText}</ReactMarkdown>
                  </div>
                </div>
              </div>
            )}

            {isLoading && !streamingText && (
              <div className="flex justify-start mb-3">
                <div className="rounded-lg px-4 py-2.5 bg-[#1A1A1A] border border-[#2A2A2A]">
                  <div className="flex gap-1">
                    <span className="w-1.5 h-1.5 bg-[#E85D24] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-1.5 h-1.5 bg-[#E85D24] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-1.5 h-1.5 bg-[#E85D24] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Phone prompt with validation */}
          {showPhonePrompt && !handoffComplete && (
            <div className="px-4 py-3 bg-[#0D0D0D] border-t border-[#2A2A2A]" data-testid="phone-prompt">
              <label className="text-[12px] text-[#aaa] mb-1.5 block">What's your WhatsApp number so our team can reach you?</label>
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <input
                    ref={phoneRef}
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="tel-national"
                    value={phoneInput}
                    onChange={handlePhoneInputChange}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleWhatsAppHandoff(); } }}
                    placeholder="e.g. 9876543210"
                    data-testid="phone-input"
                    style={{ fontSize: "16px" }}
                    className={`w-full px-3 py-2 bg-[#111] border rounded-md text-white outline-none placeholder-[#555] min-h-[44px] ${
                      phoneError ? "border-[#FF3B30] focus:border-[#FF3B30]" : "border-[#333] focus:border-[#E85D24]"
                    }`}
                  />
                </div>
                <button
                  onClick={handleWhatsAppHandoff}
                  disabled={!phoneInput.trim()}
                  data-testid="phone-send-btn"
                  className="px-4 py-2 bg-[#E85D24] text-white text-[13px] font-semibold rounded-md hover:opacity-85 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Send
                </button>
              </div>
              {phoneError && (
                <div className="flex items-center gap-1.5 mt-1.5" data-testid="phone-error">
                  <Warning size={12} className="text-[#FF3B30] flex-shrink-0" />
                  <p className="text-[11px] text-[#FF3B30]">{phoneError}</p>
                </div>
              )}
            </div>
          )}

          {/* Callback form */}
          {showCallbackForm && (
            <div className="px-4 py-3 bg-[#0D0D0D] border-t border-[#2A2A2A]" data-testid="callback-form">
              <label className="text-[12px] text-[#aaa] mb-2 block">We'll call you back — just need a few details:</label>
              <div className="space-y-2">
                <div className="relative">
                  <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555]" />
                  <input
                    type="text"
                    autoComplete="name"
                    value={callbackName}
                    onChange={(e) => setCallbackName(e.target.value)}
                    placeholder="Your name"
                    data-testid="callback-name-input"
                    style={{ fontSize: "16px" }}
                    className="w-full pl-9 pr-3 py-2 bg-[#111] border border-[#333] rounded-md text-white outline-none focus:border-[#E85D24] placeholder-[#555] min-h-[44px]"
                  />
                </div>
                <div className="relative">
                  <EnvelopeSimple size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555]" />
                  <input
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    value={callbackEmail}
                    onChange={(e) => setCallbackEmail(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleRequestCallback(); } }}
                    placeholder="Your email"
                    data-testid="callback-email-input"
                    style={{ fontSize: "16px" }}
                    className="w-full pl-9 pr-3 py-2 bg-[#111] border border-[#333] rounded-md text-white outline-none focus:border-[#E85D24] placeholder-[#555] min-h-[44px]"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowCallbackForm(false)}
                    className="flex-1 py-2 border border-[#333] text-[#888] text-[12px] font-medium rounded-md hover:border-[#555] transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleRequestCallback}
                    disabled={!callbackName.trim() || !callbackEmail.trim() || callbackSubmitting}
                    data-testid="callback-submit-btn"
                    className="flex-1 py-2 bg-[#E85D24] text-white text-[12px] font-semibold rounded-md hover:opacity-85 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {callbackSubmitting ? "Submitting..." : "Request Call-back"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* WhatsApp handoff button */}
          {showWhatsAppButton && (
            <div className="px-4 py-2 bg-[#0A0A0A] border-t border-[#2A2A2A]" data-testid="whatsapp-handoff-bar">
              <button
                onClick={() => setShowPhonePrompt(true)}
                data-testid="whatsapp-handoff-btn"
                className="w-full py-2.5 border border-[#25D366] text-[#25D366] text-[13px] font-semibold rounded-lg flex items-center justify-center gap-2 hover:bg-[#25D366] hover:text-black transition-colors"
              >
                <WhatsappLogo size={18} weight="bold" />
                Continue with our team on WhatsApp
              </button>
            </div>
          )}

          {/* Input bar */}
          {(!showPhonePrompt || handoffComplete) && !showCallbackForm && (
            <div className="p-3 border-t border-[#2A2A2A] bg-[#0A0A0A]">
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  autoComplete="off"
                  autoCapitalize="sentences"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder="Type your message..."
                  disabled={isLoading}
                  data-testid="chat-input"
                  style={{ fontSize: "16px" }}
                  className="flex-1 px-4 py-2.5 bg-[#111] border border-[#2A2A2A] rounded-lg text-white placeholder-[#555] focus:outline-none focus:border-[#E85D24] disabled:opacity-50 min-h-[44px]"
                />
                <button
                  onClick={sendMessage}
                  disabled={isLoading || !input.trim()}
                  data-testid="chat-send-btn"
                  className="px-4 py-2.5 bg-[#E85D24] text-white rounded-lg hover:opacity-85 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <PaperPlaneTilt size={18} weight="bold" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* FAB */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-testid="chat-toggle-btn"
        className="w-14 h-14 rounded-full bg-[#E85D24] text-white shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center hover:scale-105"
        style={{ boxShadow: "0 4px 20px rgba(232,93,36,0.4)" }}
      >
        {isOpen ? <X size={24} weight="bold" /> : <ChatCircle size={24} weight="bold" />}
      </button>
    </div>
  );
};

export default ChatWidget;
